import type {
    ColDef,
    ColGroupDef,
    FilterChangedEvent,
    GridApi,
    GridOptions,
    GridReadyEvent,
    ICellRendererParams,
    IDetailCellRendererParams,
    IServerSideDatasource,
    IServerSideGetRowsParams,
    IServerSideSelectionState,
    RowDataUpdatedEvent,
    RowGroupOpenedEvent,
    SelectionChangedEvent,
} from '@ag-grid-community/core';
import { AgGridReact, type AgGridReactProps } from '@ag-grid-community/react';
import { useMergeRefs } from '@floating-ui/react';
import { LoadingIndicator, type MenuItem } from '@ics-portal/guardian';
import { default as clsx } from 'clsx';
import {
    type ForwardedRef,
    forwardRef,
    type LegacyRef,
    type ReactNode,
    type RefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import CustomNumberFilter from '@components/ICSGrid/filtering/CustomNumberFilter';
import VisuallyHidden from '@components/VisuallyHidden';
import useTableSettings from '@hooks/useTableSettings';
import type { Maybe } from '@models/Maybe';
import { deselectAllDetailGrids } from '@utils/agGridUtils';
import { type CustomEmptyStateProps } from './CustomEmptyState';
import { InfiniteScrollPanel, TableRowsPanel } from './CustomStatusPanels';
import { type ColumnActionSettings } from './action-cell-renderer';
import { addNeededColumns } from './column-utils';
import {
    columnTypes as defaultColumnTypes,
    defaultConfiguration,
    masterDetails,
    multiSelect,
    singleSelect,
} from './config';
import { createQueryParamFromFilterModel } from './filtering/filter-to-query-param';
import { createFilterFromQueryParams } from './filtering/query-param-to-filter';
import {
    getFilterableColumnIds,
    onDetailsSelectionChangedGenerator,
} from './utils';
import { ICSGridControls } from './GridControls/ICSGridControls';
import {
    ICSGridActionsProvider,
    useChildGridActions,
} from './GridControls/ICSGridActionsContext';

export type ICSAgGridEmptyState<T = any> = Omit<
    CustomEmptyStateProps<T>,
    'api'
>;
export * from './action-cell-renderer';
export { getTableEmptyState } from './getTableEmptyState';

export type GridColDef<T> = ColDef<T> | ColGroupDef<T>;

export type ICSAgGridColDef<T> = GridColDef<T>[] | null | undefined;

function ICSGridLoadingOverlay(): ReactNode {
    return (
        <div className="ics-grid-loading-overlay">
            <LoadingIndicator variant="ripple" />
        </div>
    );
}

function ICSGridLoadingCellRenderer(): null {
    return null;
}

export interface ContextMenuSettings {
    hideClearFilters?: boolean;
}

export interface ActionOptionSettings {
    className?: string;
    actions: ReactNode[];
    disable?: boolean;
}

export interface ICSAgGridProps<TData> extends AgGridReactProps<TData> {
    /**
     * Reference to the AgGridReact component
     */
    ref?: RefObject<AgGridReact<TData>> &
        (LegacyRef<AgGridReact<TData>> | undefined);
    /**
     * Description of the table for screen readers
     */
    tableDescription: string;
    /**
     * Id of wrapper
     */
    id?: string;
    /**
     * Custom actions to be displayed together with the table settings
     */
    actionOption?: ActionOptionSettings;
    /**
     * A custom message to display to the user if there are no rows
     */
    emptyState?: ICSAgGridEmptyState<TData>;
    /**
     * Context menu items settings
     */
    contextMenuSettings?: ContextMenuSettings;
    /**
     * Custom context menu items
     */
    contextMenuItems?: (params: ICellRendererParams<TData>) => MenuItem[];
    /**
     * Column action settings
     */
    columnActionSettings?: ColumnActionSettings<TData>;
    /**
     * Class for wrapper of table
     */
    wrapperClassName?: string;
    /**
     * Function used to count elements in expandable rows
     */
    counter?: (params: ICellRendererParams<TData>) => number;
    /**
     * Defines if row is expandable (if it has childrens)
     */
    expandable?: boolean;
    /**
     * Header name for the detail toggler column
     */
    expandColumnHeader?: string;
    /**
     * Defines if rows can be selected
     */
    selectable?: boolean;
    /**
     * Define if table has details, so padding will be added to first column if needed
     */
    childTable?: boolean;
    /**
     * Define matser details grid options
     */
    detailCellRendererParams?:
        | (Partial<ICSAgGridProps<TData>> &
              Partial<IDetailCellRendererParams<TData>> &
              Partial<GridOptions<TData>>)
        | ((
              params: ICellRendererParams,
          ) => Partial<ICSAgGridProps<TData>> &
              Partial<IDetailCellRendererParams<TData>> &
              Partial<GridOptions<TData>>);
    /**
     * Hide context menu column
     */
    suppressMenuColumn?: boolean;
    /**
     * Binds selected rows to context={{ selectedRows }}
     */
    bindSelectedRowsToContext?: boolean;
    /**
     * Custom filter control (e.g. a Dropdown) rendered above the grid, before the built-in search box.
     */
    filterSlot?: ReactNode;
    /**
     * Overrides the default placeholder shown in the built-in grid search input.
     */
    searchPlaceholder?: string;
    /**
     * Hide the built-in grid search while keeping other grid actions visible.
     */
    hideSearch?: boolean;
    /**
     * Show AG Grid floating filters below column headers.
     */
    showFloatingFilters?: boolean;
    /**
     * Called when the global (no-column) search text changes in ICSGridSearch.
     * Useful for server-side grids that need to forward free-text to their datasource.
     */
    onGlobalFilterChange?: (term: string) => void;
}

function hasSelectionCheckboxes<T>({
    rowSelection,
    selectable,
}: ICSAgGridProps<T>): boolean {
    if (selectable === false) {
        return false;
    }

    if (!rowSelection) {
        return false;
    }

    if (typeof rowSelection === 'string') {
        return true;
    }

    return rowSelection.checkboxes !== false;
}

function addPaddingToChildTable<T>(
    columnDefs: GridColDef<T>[],
    props: ICSAgGridProps<T>,
): GridColDef<T>[] {
    const firstCol = columnDefs[0];

    const firstColIsColDef = (col?: GridColDef<T> | null): col is ColDef<T> =>
        !Object.hasOwn(col ?? {}, 'children');

    if (
        !props.childTable ||
        hasSelectionCheckboxes(props) ||
        !firstColIsColDef(firstCol)
    ) {
        return columnDefs;
    }

    const cellStyle =
        typeof firstCol.cellStyle === 'object'
            ? { ...firstCol.cellStyle, paddingLeft: '65px' }
            : { paddingLeft: '65px' };

    return [
        {
            ...firstCol,
            width: firstCol.width ? firstCol.width + 65 : firstCol.width,
            minWidth: firstCol.minWidth
                ? firstCol.minWidth + 65
                : firstCol.minWidth,
            maxWidth: firstCol.maxWidth
                ? firstCol.maxWidth + 65
                : firstCol.maxWidth,
            cellStyle,
            headerClass: clsx(firstCol.headerClass, 'child-table'),
        },
        ...columnDefs.slice(1),
    ];
}

// TODO [TECH-DEBT] Filters from child tables are applied to query params -> parent table catches it and applies to its own query
// TODO [TECH-DEBT] Saving column settings of child tables doesn't work
function ICSAgGridComponent<TData>(
    props: ICSAgGridProps<TData>,
    forwardedRef: ForwardedRef<AgGridReact<TData>>,
): ReactNode {
    const {
        tableDescription,
        actionOption,
        detailCellRendererParams,
        serverSideDatasource,
        onSelectionChanged,
        onRowDataUpdated,
        onGridReady,
        onGridSizeChanged,
        onFilterChanged,
        columnTypes,
        columnDefs: colDefs,
        emptyState = { title: "There's no data to display" },
        rowData,
        components,
        contextMenuItems,
        contextMenuSettings,
        columnActionSettings,
        counter,
        expandable,
        expandColumnHeader,
        selectable,
        childTable,
        onStateUpdated,
        rowSelection,
        wrapperClassName: wrapperClassNameProps,
        defaultColDef,
        id,
        bindSelectedRowsToContext,
        hideSearch,
        showFloatingFilters,
        onGlobalFilterChange,
        filterSlot,
        searchPlaceholder,
        context: contextProps,
        ...restProps
    } = props;
    const internalRef = useRef<AgGridReact | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const refs = useMergeRefs([internalRef, forwardedRef]);

    const gridId = `${window.location.pathname}-${id ?? tableDescription}`;
    const { commonGridOptions } = useTableSettings(internalRef, gridId);

    const [searchParams] = useSearchParams();
    const [selectedRows, setSelectedRows] = useState<TData[]>([]);
    const [api, setApi] = useState(internalRef.current?.api);
    const serverSideRequestIdRef = useRef(0);
    const context = useMemo(
        () => ({ ...contextProps, selectedRows }),
        [contextProps, selectedRows],
    );

    const mergedColumnTypes = columnTypes
        ? { ...defaultColumnTypes, ...columnTypes }
        : defaultColumnTypes;

    const columnDefs = colDefs
        ? addNeededColumns(
              addPaddingToChildTable(colDefs, props),
              props,
              mergedColumnTypes,
          )
        : colDefs;

    const contextMenuItemsRef = useRef(contextMenuItems);
    contextMenuItemsRef.current = contextMenuItems;
    const contextPropsRef = useRef(contextProps);
    contextPropsRef.current = contextProps;
    // Captured once at mount — initial state never changes
    const initialStateRef = useRef(commonGridOptions.initialState);

    const { setChildMenuItems, setChildSearchTarget } = useChildGridActions();

    useEffect(() => {
        if (!childTable || !api) return;
        setChildSearchTarget({ api, columnDefs: colDefs ?? [] });
    }, [childTable, api, colDefs, setChildSearchTarget]);

    useEffect(() => {
        if (!childTable || !contextMenuItemsRef.current || !api) return;

        const resolveSelectedRows = (): TData[] => {
            const selected = api.getSelectedRows();
            if (selected.length > 0) return selected;

            const initialIds = (
                initialStateRef.current
                    ?.rowSelection as Maybe<IServerSideSelectionState>
            )?.toggledNodes;

            if (!initialIds?.length) return [];

            return initialIds
                .map(
                    (rowId) => api.getRowNode(rowId)?.data as TData | undefined,
                )
                .filter((d): d is TData => d !== null && d !== undefined);
        };

        setChildMenuItems(
            contextMenuItemsRef.current({
                context: {
                    ...contextPropsRef.current,
                    selectedRows: resolveSelectedRows(),
                },
                api,
            } as unknown as ICellRendererParams<TData>),
        );
    }, [childTable, api, selectedRows, setChildMenuItems]);

    const handleDocumentClick = useCallback((e: MouseEvent): void => {
        const mainApi = internalRef.current?.api;
        if (!mainApi) {
            return;
        }

        const target = e.target as HTMLElement;
        if (typeof target.className !== 'string') {
            return;
        }

        const isExpandCollapseClick =
            target.className.includes('ag-icon-tree-');
        const clickOutsideEveryToolPanel = Array.from(
            document.querySelectorAll('.ag-tool-panel-wrapper').values(),
        ).every((toolPanel) => !toolPanel.contains(target));

        if (!clickOutsideEveryToolPanel || isExpandCollapseClick) {
            return;
        }

        const isToolPanelButton =
            target.className.includes('ag-side-button-button') ||
            target.parentElement?.parentElement?.className.includes(
                'ag-side-button-button',
            ) ||
            target.parentElement?.className.includes('ag-side-button-button');
        if (!isToolPanelButton) {
            mainApi.closeToolPanel();
            mainApi.forEachDetailGridInfo((detailGridInfo) => {
                if (!detailGridInfo.api?.isDestroyed()) {
                    detailGridInfo.api?.closeToolPanel();
                }
            });
        }
    }, []);

    useEffect(() => {
        document.addEventListener('click', handleDocumentClick);

        return () => {
            document.removeEventListener('click', handleDocumentClick);
        };
    }, [internalRef]);

    const handleOnGridReady = (event: GridReadyEvent<TData>): void => {
        /**
         * Set the filter model based on query parameters
         * Essential for being able to link to a specific filtered view
         * i.e URLSearchParams to filterModel
         * @see {@link handleOnFilterChange} for its counterpart to save the filter model into query parameters
         */
        setApi(event.api);
        requestAnimationFrame(() => {
            updateLoadingOverlayTop();
        });
        if (columnDefs) {
            // We only want to match any query parameter keys that match our column ids
            const colIds = getFilterableColumnIds(columnDefs);
            // Create the filter models from the query params
            const filterModel = createFilterFromQueryParams(
                colIds,
                searchParams,
            );
            // Update the AG-Grid filter
            event.api.setFilterModel(filterModel);

            if (!childTable && Object.keys(filterModel).length > 0) {
                requestAnimationFrame(() => {
                    wrapperRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });
                });
            }
        }

        /**
         * Call the user provided "onGridReady" callback if it's defined
         */
        if (typeof onGridReady === 'function') {
            onGridReady(event);
        }
    };

    const updateLoadingOverlayTop = useCallback((): void => {
        const headerHeight =
            wrapperRef.current?.querySelector<HTMLElement>('.ag-header')
                ?.offsetHeight ?? 0;

        wrapperRef.current?.style.setProperty(
            '--ics-ag-loading-overlay-top',
            `${String(headerHeight)}px`,
        );
    }, []);

    const handleOnFilterChange = (event: FilterChangedEvent<TData>): void => {
        updateUrlParams(event);

        /**
         * Call the user provided "onFilterChanged" callback if it's defined
         */
        if (typeof onFilterChanged === 'function') {
            onFilterChanged(event);
        }
    };

    /**
     * Updates URL search parameters without triggering a React re-render.
     * This prevents focus loss in filter inputs when filters change.
     */
    const updateUrlParams = (event: FilterChangedEvent<TData>): void => {
        const url = new URL(window.location.href);
        const currentParams = url.searchParams;
        const filterModel = event.api.getFilterModel();
        const colIds = columnDefs ? getFilterableColumnIds(columnDefs) : [];
        const newParams = createQueryParamFromFilterModel(
            filterModel,
            currentParams,
            colIds,
        );
        url.search = newParams.toString();
        window.history.replaceState(null, '', url.toString());
    };

    const syncSelectedRows = useCallback((gridApi: GridApi<TData>) => {
        const selectionState =
            gridApi.getServerSideSelectionState() as Maybe<IServerSideSelectionState>;
        const isServerSideSelectAll =
            gridApi.getGridOption('rowModelType') === 'serverSide' &&
            Boolean(selectionState?.selectAll);
        const selected = isServerSideSelectAll ? [] : gridApi.getSelectedRows();
        setSelectedRows(selected);
    }, []);

    const handleRowDataUpdated = (event: RowDataUpdatedEvent<TData>): void => {
        if (bindSelectedRowsToContext || childTable) {
            syncSelectedRows(event.api);
        }
        if (typeof onRowDataUpdated === 'function') {
            onRowDataUpdated(event);
        }
    };

    const handleOnSelectionChanged = (
        event: SelectionChangedEvent<TData>,
    ): void => {
        const selectionState =
            event.api.getServerSideSelectionState() as Maybe<IServerSideSelectionState>;
        const isServerSideSelectAll =
            event.api.getGridOption('rowModelType') === 'serverSide' &&
            Boolean(selectionState?.selectAll);
        const selectedRows = isServerSideSelectAll
            ? []
            : event.api.getSelectedRows();
        if (selectedRows.length > 0) {
            deselectAllDetailGrids(event.api);
            event.api.forEachNode((node) => {
                if (node.expanded) {
                    event.api.setRowNodeExpanded(node, false);
                }
            });
        }

        const isProgrammatic = event.source === 'api';
        if (bindSelectedRowsToContext || (childTable && !isProgrammatic)) {
            setSelectedRows(selectedRows);
        }

        /**
         * Call the user provided "onSelectionChanged" callback if it's defined
         */
        if (typeof onSelectionChanged === 'function') {
            onSelectionChanged(event);
        }
    };

    useEffect(() => {
        if (!api || childTable) return;
        const handleRowGroupOpened = (
            event: RowGroupOpenedEvent<TData>,
        ): void => {
            if (event.expanded) {
                api.deselectAll();
            }
        };
        api.addEventListener('rowGroupOpened', handleRowGroupOpened);
        return () => {
            if (!api.isDestroyed())
                api.removeEventListener('rowGroupOpened', handleRowGroupOpened);
        };
    }, [api, childTable]);

    // If detailCellRendererParams is supplied we automatically set default options for nested grids
    const masterDetailsOptions: GridOptions<TData> = useMemo(() => {
        if (!detailCellRendererParams) {
            return {};
        }
        if (typeof detailCellRendererParams === 'function') {
            return {
                ...masterDetails,
                detailCellRendererParams: (params: ICellRendererParams) => {
                    const computedDetailCellRenderParams =
                        detailCellRendererParams(params);

                    const detailColumnDefs = addNeededColumns(
                        computedDetailCellRenderParams.detailGridOptions
                            ?.columnDefs ?? [],
                        {
                            tableDescription: `details:${tableDescription}`,
                            ...computedDetailCellRenderParams.detailGridOptions,
                        },
                        mergedColumnTypes,
                    );
                    return {
                        ...computedDetailCellRenderParams,
                        detailGridOptions: {
                            gridId: `detail-${gridId}`,
                            ...commonGridOptions,
                            ...computedDetailCellRenderParams.detailGridOptions,
                            paginationPageSize: 10,
                            onGridPreDestroyed: () => {},
                            onSelectionChanged:
                                onDetailsSelectionChangedGenerator(
                                    (e) =>
                                        computedDetailCellRenderParams.detailGridOptions?.onSelectionChanged?.(
                                            e,
                                        ),
                                    internalRef,
                                ),
                            components: {
                                ...(computedDetailCellRenderParams.components ??
                                    {}),
                            },
                            columnDefs: detailColumnDefs,
                        },
                    };
                },
            } as GridOptions<TData>;
        }
        const detailColumnDefs = addNeededColumns(
            detailCellRendererParams.detailGridOptions?.columnDefs ?? [],
            {
                tableDescription: `details:${tableDescription}`,
                ...detailCellRendererParams.detailGridOptions,
            },
            mergedColumnTypes,
        );
        return {
            detailCellRendererParams: {
                ...defaultConfiguration,
                ...detailCellRendererParams,
                detailGridOptions: {
                    gridId: `detail-${gridId}`,
                    ...commonGridOptions,
                    ...detailCellRendererParams.detailGridOptions,
                    paginationPageSize: 10,
                    onGridPreDestroyed: () => {},
                    onSelectionChanged: onDetailsSelectionChangedGenerator(
                        (e) =>
                            detailCellRendererParams.detailGridOptions?.onSelectionChanged?.(
                                e,
                            ),
                        internalRef,
                    ),
                    components: {
                        ...(detailCellRendererParams.components ?? {}),
                    },
                    columnDefs: detailColumnDefs,
                },
            },
            ...masterDetails,
        };
    }, [detailCellRendererParams]);

    const serverSideInitialRowCount =
        typeof restProps.paginationPageSize === 'number'
            ? restProps.paginationPageSize
            : commonGridOptions.paginationPageSize ?? 20;

    const wrappedServerSideDatasource = useMemo<
        IServerSideDatasource | undefined
    >(() => {
        if (!serverSideDatasource) {
            return undefined;
        }

        return {
            getRows: (params: IServerSideGetRowsParams): void => {
                const requestId = serverSideRequestIdRef.current + 1;
                serverSideRequestIdRef.current = requestId;
                updateLoadingOverlayTop();
                params.api.setGridOption('loading', true);

                serverSideDatasource.getRows({
                    ...params,
                    success: (successParams) => {
                        params.success(successParams);

                        if (
                            serverSideRequestIdRef.current !== requestId ||
                            params.api.isDestroyed()
                        ) {
                            return;
                        }

                        params.api.setGridOption('loading', false);

                        if (successParams.rowCount === 0) {
                            params.api.showNoRowsOverlay();
                            return;
                        }

                        params.api.hideOverlay();
                    },
                    fail: () => {
                        params.fail();

                        if (
                            serverSideRequestIdRef.current !== requestId ||
                            params.api.isDestroyed()
                        ) {
                            return;
                        }

                        params.api.setGridOption('loading', false);
                    },
                });
            },
            destroy: () => {
                serverSideDatasource.destroy?.();
            },
        };
    }, [serverSideDatasource, updateLoadingOverlayTop]);

    const isInfiniteScroll =
        serverSideDatasource && restProps.pagination === false;

    // If serverSideDatasource is supplied we automatically set default options for server side grids
    const serverSideOptions: GridOptions<TData> = serverSideDatasource
        ? {
              serverSideDatasource: wrappedServerSideDatasource,
              rowModelType: 'serverSide' as const,
              serverSideInitialRowCount,
              loadingOverlayComponent: ICSGridLoadingOverlay,
              loadingCellRenderer: ICSGridLoadingCellRenderer,
              suppressServerSideFullWidthLoadingRow: true,
              statusBar: {
                  statusPanels: [
                      {
                          statusPanel: isInfiniteScroll
                              ? InfiniteScrollPanel
                              : TableRowsPanel,
                          key: 'ICSTableRowsPanel',
                          align: 'left',
                      },
                  ],
              },
          }
        : {};

    const restAgGridOptions: GridOptions<TData> = {
        ...masterDetailsOptions,
        ...serverSideOptions,
        // Overwrite or additional props
        ...restProps,
        components: {
            ...components,
            //gTextColumnFloatingFilter: CustomTextFilter,
            agNumberColumnFloatingFilter: CustomNumberFilter,
            rowActions: components?.rowActions ?? (() => null),
        },
    };

    const wrapperClassName = clsx('ag-theme-alpine', wrapperClassNameProps);

    const rowSelectionMode = useMemo(() => {
        if (typeof rowSelection !== 'string') {
            return rowSelection;
        }

        if (selectable === false) {
            return undefined;
        }

        return rowSelection === 'multiple'
            ? multiSelect.rowSelection
            : singleSelect.rowSelection;
    }, [rowSelection, selectable]);

    const defaultColDefMerged: Omit<ColDef, 'field' | 'tooltipField'> =
        useMemo(() => {
            if (defaultColDef) {
                return {
                    ...defaultColDef,
                    ...defaultConfiguration.defaultColDef,
                };
            }
            return defaultConfiguration.defaultColDef;
        }, [defaultColDef]);
    const isLoading = Boolean(restProps.loading);
    useEffect(() => {
        const api = internalRef.current?.api;
        if (!api || serverSideDatasource) return;

        setTimeout(() => {
            if (isLoading) return;
            if (rowData?.length === 0) {
                api.showNoRowsOverlay();
            } else {
                api.hideOverlay();
            }
        }, 0);
    }, [rowData, serverSideDatasource, isLoading]);

    const content = (
        <>
            <VisuallyHidden id="table-description">
                {tableDescription}
            </VisuallyHidden>
            {!actionOption?.disable && Boolean(actionOption?.actions) && (
                <div
                    className={clsx(
                        'flex gap-1 justify-content-end pb-md',
                        actionOption?.className,
                    )}
                >
                    {actionOption?.actions}
                </div>
            )}

            {childTable ? null : (
                <ICSGridControls
                    {...props}
                    context={context}
                    api={api}
                    columnDefs={colDefs}
                    hideSearch={hideSearch}
                    searchParams={searchParams}
                />
            )}

            <div id={id} ref={wrapperRef} className={wrapperClassName}>
                <AgGridReact<TData>
                    ref={refs}
                    gridId={gridId}
                    // Shared grid options
                    {...commonGridOptions}
                    //Options for master grid
                    defaultColDef={defaultColDefMerged}
                    columnDefs={columnDefs}
                    columnTypes={mergedColumnTypes}
                    onGridReady={handleOnGridReady}
                    noRowsOverlayComponentParams={emptyState}
                    onSelectionChanged={handleOnSelectionChanged}
                    onRowDataUpdated={handleRowDataUpdated}
                    onFilterChanged={handleOnFilterChange}
                    rowData={rowData}
                    rowSelection={rowSelectionMode}
                    // Rest of the props
                    context={context}
                    {...restAgGridOptions}
                    floatingFiltersHeight={showFloatingFilters ? undefined : 0}
                    enableAdvancedFilter={false}
                    statusBar={restAgGridOptions.statusBar ?? undefined}
                />
            </div>
        </>
    );

    return childTable ? (
        content
    ) : (
        <ICSGridActionsProvider>{content}</ICSGridActionsProvider>
    );
}

export const ICSAgGrid = forwardRef(ICSAgGridComponent);
